import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { I18nService } from '../shared/i18n/i18n.service';
import { ErrorService } from '../shared/services/error/error.service';

const CLASS_NAME = 'ApiErrorInterceptor';
const API_ERRORS_SCOPE = 'api_errors';

export const apiErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const i18n = inject(I18nService);
  const errorService = inject(ErrorService);
  const isBackendRequest = isBackendApiRequest(req.url);

  return next(req).pipe(
    // catch any errors
    catchError((error: unknown) => {
      // if not http error, let the error fall through
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      // Angular uses status=0 for network-level failures (offline/CORS/DNS),
      // so we handle it like a generic backend connectivity error.
      const isNetworkError = error.status === 0;
      const isHttpFailure = error.status >= 400;
      if (!isBackendRequest || (!isNetworkError && !isHttpFailure)) {
        return throwError(() => error);
      }

      const messageKey = getErrorMessageKey(error);
      errorService.handle(error, {
        userMessage: i18n.t(messageKey, API_ERRORS_SCOPE),
        context: {
          source: CLASS_NAME,
          action: 'intercept',
          metadata: {
            method: req.method,
            url: req.url,
            status: error.status,
          },
        },
      });
      return throwError(() => error);
    }),
  );
};

function getErrorMessageKey(error: HttpErrorResponse): string {

  switch (error.status) {
    case 0:
      return 'api_error_network';
    case 400:
      return 'api_error_bad_request';
    case 401:
      return 'api_error_unauthorized';
    case 403:
      return 'api_error_forbidden';
    case 404:
      return 'api_error_not_found';
    case 409:
      return 'api_error_conflict';
    case 413:
      return 'api_error_payload_too_large';
    default:
      if (error.status >= 500) return 'api_error_server';
      return 'api_error_generic';
  }
}

function isBackendApiRequest(url: string): boolean {
  const apiBase = environment.backendUrls.api;
  if (url.startsWith(apiBase)) {
    return true;
  }
  return url.includes(`${apiBase}/`);
}
